import {
  ComponentRendering,
  Field,
  Image,
  ImageField,
  LinkField,
  RichTextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';

import { ComponentProps, useFallbackTranslation, withTheme } from '@gn-audio/jss-components';

type CaseStudyNavigationFields = {
  prevCSLink: LinkField;
  prevCSBackground: ImageField;
  prevCSLogo: ImageField;
  prevCSDescription: Field<string>;
  nextCSLink: LinkField;
  nextCSBackground: ImageField;
  nextCSLogo: ImageField;
  nextCSDescription: Field<string>;
  relatedLinks: RichTextField;
};

type CaseStudyNavigationProps = ComponentProps & {
  fields: CaseStudyNavigationFields;
};

const CaseStudyNavigation = ({ fields }: CaseStudyNavigationProps) => {
  const { t } = useFallbackTranslation();
  const {
    prevCSLink,
    prevCSBackground,
    prevCSLogo,
    prevCSDescription,
    nextCSLink,
    nextCSBackground,
    nextCSLogo,
    nextCSDescription,
    relatedLinks,
  } = fields;

  const prevStoryData = {
    link: prevCSLink,
    background: prevCSBackground,
    logo: prevCSLogo,
    description: prevCSDescription,
    variantTitle: t('system.PreviousStory'),
  };

  const nextStoryData = {
    link: nextCSLink,
    background: nextCSBackground,
    logo: nextCSLogo,
    description: nextCSDescription,
    variantTitle: t('system.NextStory'),
  };

  return (
    <div className="container">
      <nav className="mb-16 flex flex-col gap-6 lg:flex-row">
        {[prevStoryData, nextStoryData].map(
          ({ link, background, logo, description, variantTitle }, index) => {
            if (!link?.value?.href) {
              return null;
            }

            return (
              <a
                key={`case-study-${index}`}
                href={link?.value?.href}
                className="relative flex min-h-80 w-full flex-col overflow-hidden rounded-xl pt-9 transition-opacity hover:no-underline hover:opacity-85 lg:aspect-[16/9] lg:w-1/2"
              >
                {background?.value && (
                  <Image
                    field={background}
                    className="absolute inset-0 z-0 h-full w-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
                {logo?.value && (
                  <div className="w-1/2">
                    <div className="relative z-10 flex w-fit items-center overflow-hidden rounded-r-full bg-white">
                      <Image field={logo} className="px-6 py-4" />
                    </div>
                  </div>
                )}
                <div className="relative z-10 flex flex-1 flex-col justify-end p-6 text-white">
                  <div className="font-bold uppercase">{variantTitle}</div>
                  {description?.value && <div className="text-2xl">{description?.value}</div>}
                </div>
              </a>
            );
          }
        )}
      </nav>
      <section>
        <h2 className="mb-6 text-2xl font-bold">{t('system.RelatedLinks')}</h2>
        <div
          className="flex flex-col gap-3 lg:flex-row lg:gap-6 [&>a]:underline [&>a]:transition-opacity hover:[&>a]:opacity-80"
          dangerouslySetInnerHTML={{ __html: relatedLinks?.value as string }}
        />
      </section>
    </div>
  );
};

export const getStaticProps = async (rendering: ComponentRendering<CaseStudyNavigationFields>) => {
  try {
    const { fields: dataSource, params = {} } = rendering;
    if (!dataSource) {
      throw new Error('No fields found for Case Study Navigation');
    }
    const fields = {
      ...dataSource,
      relatedLinks: {
        ...dataSource.relatedLinks,
        value: sanitize((dataSource.relatedLinks?.value as string) || ''),
      },
    };

    return {
      fields,
      params,
    };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { params };
  }
};

export default withTheme()(withDatasourceCheck()(CaseStudyNavigation));
